export type Role = 'admin' | 'release_manager';

export interface ProjectRef {
  id: number;
  name: string;
}

export interface User {
  id: number;
  username: string;
  name: string;
  role: Role;
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
  projects: ProjectRef[];
}

export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Admin',
  release_manager: 'Release Manager',
};

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

function errorMessage(body: unknown, status: number): string {
  const detail = (body as { detail?: unknown } | null)?.detail;
  if (typeof detail === 'string') return detail;
  // FastAPI validation errors: [{loc, msg}, ...]
  if (Array.isArray(detail) && detail[0]?.msg) return String(detail[0].msg);
  return `Request failed (${status})`;
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json');

  const response = await fetch(path, { ...init, headers, credentials: 'same-origin' });
  if (response.status === 204) return undefined as T;

  const body = await response.json().catch(() => null);
  if (!response.ok) throw new ApiError(response.status, errorMessage(body, response.status));
  return body as T;
}

export const authApi = {
  me: () => api<User>('/api/auth/me'),
  login: (username: string, password: string) =>
    api<User>('/api/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) }),
  logout: () => api<void>('/api/auth/logout', { method: 'POST' }),
  updateMe: (name: string) => api<User>('/api/auth/me', { method: 'PATCH', body: JSON.stringify({ name }) }),
  changePassword: (current_password: string, new_password: string) =>
    api<void>('/api/auth/password', {
      method: 'POST',
      body: JSON.stringify({ current_password, new_password }),
    }),
};

// --- domain types (mirror backend/app/schemas.py) ---

export interface UserRef {
  id: number;
  username: string;
  name: string;
}

export interface HardwareType {
  code: string;
  name: string;
  chip_id: number | null;
  default_project_id: number | null;
}

export interface Group {
  id: number;
  project_id: number;
  name: string;
  description: string;
  default_release_id: number | null;
  device_count: number;
}

export interface Project {
  id: number;
  name: string;
  slug: string;
  description: string;
  hardware_type: string;
  default_group_id: number | null;
  created_at: string;
  members: UserRef[];
  groups: Group[];
  device_count: number;
  release_count: number;
}

export type ReleaseStatus = 'active' | 'deprecated' | 'blocked';

export interface Release {
  id: number;
  project_id: number;
  version: string;
  label: string;
  description: string;
  changelog: string;
  status: ReleaseStatus;
  size: number;
  sha256: string;
  image_version: string | null;
  image_project: string | null;
  image_build_date: string | null;
  image_idf_version: string | null;
  uploaded_by: number | null;
  created_at: string;
  target_count: number;
  installed_count: number;
  unhealthy_count: number;
  warnings: string[];
}

export interface ImageInspection {
  chip: string | null;
  image_version: string | null;
  image_project: string | null;
  image_build_date: string | null;
  image_idf_version: string | null;
  errors: string[];
  warnings: string[];
}

export type HealthLevel = 'ok' | 'warning' | 'critical' | 'unknown';
export type DeviceState = 'up_to_date' | 'updating' | 'failed' | 'no_target';

export interface Device {
  id: number;
  serial_number: string;
  name: string;
  notes: string;
  hardware_type: string;
  project_id: number | null;
  group_id: number | null;
  target_release_id: number | null;
  reported_version: string | null;
  first_seen: string;
  last_seen: string | null;
  last_ip: string | null;
  last_boot_count: number | null;
  last_reset_reason: string | null;
  update_attempts: number;
  update_failed: boolean;
  effective_release_id: number | null;
  effective_version: string | null;
  target_source: 'device' | 'group' | null;
  state: DeviceState;
  health: { level: HealthLevel; unexpected_reboots: number } | null;
}

export interface Checkin {
  id: number;
  ts: string;
  reported_version: string;
  boot_count: number | null;
  reset_reason: string | null;
  ip: string | null;
  response: string;
  offered_release_id: number | null;
}

export type JobStatus = 'running' | 'paused' | 'completed' | 'cancelled';
export type JobDeviceStatus = 'pending' | 'offered' | 'done' | 'failed' | 'cancelled';

export interface UpdateJob {
  id: number;
  project_id: number;
  release_id: number;
  release_version: string;
  scope: 'selected' | 'group' | 'all';
  group_id: number | null;
  status: JobStatus;
  batch_size: number | null;
  auto_pause: boolean;
  note: string;
  created_by: number | null;
  created_by_name: string | null;
  created_at: string;
  counts: Record<JobDeviceStatus | 'total', number>;
  devices:
    | {
        device_id: number;
        serial_number: string;
        name: string;
        status: JobDeviceStatus;
        reported_version: string | null;
        offered_at: string | null;
        done_at: string | null;
      }[]
    | null;
}

export interface AuditEntry {
  id: number;
  ts: string;
  user_id: number | null;
  username: string | null;
  action: string;
  entity: string;
  entity_id: string | null;
  details: Record<string, unknown>;
}

export interface HealthSummary {
  window_hours: number;
  levels: Record<HealthLevel, number>;
  failed_updates: number;
  unstable_releases: { project_id: number; release_id: number | null; version: string; devices: number; unhealthy: number }[];
}

const json = (body: unknown) => JSON.stringify(body);
const qs = (params: Record<string, string | number | boolean | null | undefined>) => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') search.set(k, String(v));
  const s = search.toString();
  return s ? `?${s}` : '';
};

export const usersApi = {
  list: () => api<User[]>('/api/users'),
  create: (body: { username: string; name: string; password: string; role: Role; project_ids: number[] }) =>
    api<User>('/api/users', { method: 'POST', body: json(body) }),
  update: (id: number, body: Partial<{ name: string; role: Role; is_active: boolean; password: string; project_ids: number[] }>) =>
    api<User>(`/api/users/${id}`, { method: 'PATCH', body: json(body) }),
  remove: (id: number) => api<void>(`/api/users/${id}`, { method: 'DELETE' }),
};

export const projectsApi = {
  hardwareTypes: () => api<HardwareType[]>('/api/hardware-types'),
  setDefaultProject: (code: string, default_project_id: number | null) =>
    api<HardwareType>(`/api/hardware-types/${code}`, { method: 'PATCH', body: json({ default_project_id }) }),
  list: () => api<Project[]>('/api/projects'),
  get: (id: number) => api<Project>(`/api/projects/${id}`),
  create: (body: { name: string; slug?: string; description: string; hardware_type: string }) =>
    api<Project>('/api/projects', { method: 'POST', body: json(body) }),
  update: (id: number, body: Partial<{ name: string; description: string; default_group_id: number; member_ids: number[] }>) =>
    api<Project>(`/api/projects/${id}`, { method: 'PATCH', body: json(body) }),
  remove: (id: number) => api<void>(`/api/projects/${id}`, { method: 'DELETE' }),
  createGroup: (projectId: number, body: { name: string; description?: string }) =>
    api<Group>(`/api/projects/${projectId}/groups`, { method: 'POST', body: json(body) }),
  updateGroup: (id: number, body: Partial<{ name: string; description: string; default_release_id: number | null }>) =>
    api<Group>(`/api/groups/${id}`, { method: 'PATCH', body: json(body) }),
  removeGroup: (id: number) => api<void>(`/api/groups/${id}`, { method: 'DELETE' }),
};

export const releasesApi = {
  list: (projectId: number) => api<Release[]>(`/api/projects/${projectId}/releases`),
  inspect: (projectId: number, file: File, version: string) => {
    const form = new FormData();
    form.set('file', file);
    if (version) form.set('version', version);
    return api<ImageInspection>(`/api/projects/${projectId}/releases/inspect`, { method: 'POST', body: form });
  },
  upload: (projectId: number, file: File, fields: { version: string; changelog: string; label: string; description: string }) => {
    const form = new FormData();
    form.set('file', file);
    for (const [k, v] of Object.entries(fields)) form.set(k, v);
    return api<Release>(`/api/projects/${projectId}/releases`, { method: 'POST', body: form });
  },
  update: (id: number, body: Partial<{ label: string; description: string; changelog: string; status: ReleaseStatus }>) =>
    api<Release>(`/api/releases/${id}`, { method: 'PATCH', body: json(body) }),
  remove: (id: number) => api<void>(`/api/releases/${id}`, { method: 'DELETE' }),
  downloadUrl: (id: number) => `/api/releases/${id}/download`,
};

export const devicesApi = {
  list: (params: { project_id?: number; group_id?: number; unassigned?: boolean; q?: string }) =>
    api<Device[]>(`/api/devices${qs(params)}`),
  get: (id: number) => api<Device>(`/api/devices/${id}`),
  checkins: (id: number, limit = 100) => api<Checkin[]>(`/api/devices/${id}/checkins${qs({ limit })}`),
  update: (
    id: number,
    body: Partial<{ name: string; notes: string; project_id: number | null; group_id: number; clear_target: boolean }>,
  ) => api<Device>(`/api/devices/${id}`, { method: 'PATCH', body: json(body) }),
  retry: (id: number) => api<Device>(`/api/devices/${id}/retry`, { method: 'POST' }),
  remove: (id: number) => api<void>(`/api/devices/${id}`, { method: 'DELETE' }),
};

export const updatesApi = {
  list: (projectId: number) => api<UpdateJob[]>(`/api/projects/${projectId}/updates`),
  get: (id: number) => api<UpdateJob>(`/api/updates/${id}`),
  create: (
    projectId: number,
    body: {
      release_id: number;
      device_ids?: number[];
      group_id?: number;
      all?: boolean;
      batch_size?: number | null;
      auto_pause: boolean;
      note: string;
    },
  ) => api<UpdateJob>(`/api/projects/${projectId}/updates`, { method: 'POST', body: json(body) }),
  pause: (id: number) => api<UpdateJob>(`/api/updates/${id}/pause`, { method: 'POST' }),
  resume: (id: number, ignoreHealth = false) =>
    api<UpdateJob>(`/api/updates/${id}/resume${qs({ ignore_health: ignoreHealth || undefined })}`, { method: 'POST' }),
  cancel: (id: number) => api<UpdateJob>(`/api/updates/${id}/cancel`, { method: 'POST' }),
};

export const adminApi = {
  audit: (params: { limit?: number; before_id?: number }) => api<AuditEntry[]>(`/api/audit${qs(params)}`),
  health: () => api<HealthSummary>('/api/health/summary'),
};
